import { observer } from 'mobx-react-lite'

import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { type SxProps } from '@mui/material/styles'
import { Text } from '@mxenabled/mxui'

import { useGlobalCopyStore } from '../../../../common'
import { LineChartLabel } from '../LineChart'
import { getMarkerShape, MARKER_SHAPE_PATHS } from './utils/markerShapes'
import { formatCurrency } from '../../../../core/utils/NumberFormatting'

export const CustomLegendLabel = ({
  label,
  customLegendVariant,
}: {
  label: LineChartLabel
  customLegendVariant?: string
}) => {
  const variant = (customLegendVariant as any) || 'body2'

  if (typeof label === 'string') {
    return <Text variant={variant}>{label}</Text>
  }
  if (typeof label === 'object' && label.amount !== undefined && label.label) {
    return (
      <>
        <Text variant={variant}>{label.label}</Text>
        <Text bold={true} sx={{ marginLeft: 8 }} variant={variant}>
          {formatCurrency(label.amount, '0,0')}
        </Text>
      </>
    )
  }
  return null
}

const CustomLegend = ({
  average,
  customLegendVariant,
  customLegendBoxSx,
  labels = [],
  series,
  style,
  title,
  valueFormatterString,
}: {
  average?: number
  customLegendVariant?: string
  customLegendBoxSx?: SxProps
  labels?: LineChartLabel[]
  series: any[]
  title?: string
  style: any
  valueFormatterString?: string
}) => {
  const { common: commonCopy } = useGlobalCopyStore()
  const variant = (customLegendVariant as any) || 'body2'

  return (
    <Stack style={{ flexDirection: 'row', gap: 24, ...style }}>
      {series.map((s, index) => {
        const label = labels[index]
        // Skip series without a label
        return label ? (
          <Stack
            key={index}
            style={{ alignItems: 'center', flexDirection: 'row', flexWrap: 'wrap' }}
          >
            {customLegendBoxSx !== undefined ? (
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  backgroundColor: s.color,
                  marginRight: 8,
                  ...customLegendBoxSx,
                }}
              />
            ) : (
              <svg
                aria-hidden="true"
                height={14}
                style={{ marginRight: 8, flexShrink: 0 }}
                viewBox="-6 -6 12 12"
                width={14}
              >
                <path
                  d={MARKER_SHAPE_PATHS[getMarkerShape(index)]}
                  fill={s.color}
                  stroke={s.color}
                  strokeWidth={1}
                />
              </svg>
            )}
            <CustomLegendLabel customLegendVariant={customLegendVariant} label={label} />
          </Stack>
        ) : null
      })}
      {average !== undefined && (
        <Stack sx={{ alignItems: 'center', flexDirection: 'row' }}>
          <Text bold={true} sx={{ marginRight: 8 }} variant={variant}>
            – –
          </Text>
          <Text variant={variant}>{title ? title : commonCopy.average}&nbsp;&nbsp;</Text>
          <Text bold={true} variant={variant}>
            {valueFormatterString ? formatCurrency(average, valueFormatterString) : average}
          </Text>
        </Stack>
      )}
    </Stack>
  )
}

export default observer(CustomLegend)
